/**
 * The app's pages, and which of them a person may see.
 *
 * Shared by the nav rail and the command bar, so the two can never disagree
 * about what is on someone's menu: a page missing from the rail is missing
 * from search too.
 */
import {
  IconAddressBook, IconArrowBackUp, IconBell, IconBolt, IconBriefcase, IconBuilding, IconBuildingStore, IconChartBar,
  IconChecklist, IconFileInvoice, IconFilter, IconHistory, IconHome, IconList, IconMail, IconReportAnalytics, IconRoute,
  IconSend, IconSettings, IconSpeakerphone, IconTable, IconUserCircle,
} from '@tabler/icons-react';
import type { components } from '../api/schema';
import type { NavLabelKey } from '../vocab';

/**
 * Whether the organisation has money to show, from `GET /api/v1/campaigns`:
 * a stream that sells, a return or a statement.
 */
export type MoneyPresence = components['schemas']['MoneyPresence'];

// One short menu (One Rowstorm, step 2): the everyday records first, then
// "Set up" (where rows arrive and where they go, and what happens after),
// then "Money", then the record of what happened, then Settings.
//
// `labelKey` names the words, which come from the word table: /campaigns
// reads "Streams", /routes "Routes". `tab` is the key in the user's `nav_tabs`, which the
// gateway resolves from their role (a buyer or supplier gets dashboard,
// leads and reports). Those keys are stored per user and must not change,
// including the quirks kept on purpose: Activity (/logs) and Email are gated
// by `campaigns`, Imports (/sheets) by `import`, and Routes by
// `destinations` (it is the item Destinations used to be). Settings has
// none: everyone has a profile and a password to change.
//
// Nothing here is hidden by the organisation's old `features` switches any
// more: money appears where a stream sells, and everything else follows the
// login's own tabs and permissions.
//
// `rail: false` keeps a page reachable (by address, by the command bar, and
// by the links that lead to it) without an item of its own on the rail.
// `alsoActive` lights an item up for pages that are part of it.
export type NavEntry = {
  /**
   * The page's own path: what visibility, the command bar and `visiblePaths`
   * name it by. Also where the rail links, unless `href` says otherwise.
   */
  to: string;
  /**
   * Where the rail item goes, when that is a view of another page: People
   * opens the records page on each person, Buyers and Suppliers open
   * Companies filtered to a role. `to` still works as an address (it
   * redirects here), and the item lights up when this address is the one
   * open.
   */
  href?: string;
  labelKey: NavLabelKey;
  icon: typeof IconChartBar;
  tab?: string;
  rail?: false;
  /** Offered as a page in the command bar. Default true. */
  command?: false;
  alsoActive?: string[];
  /**
   * Who may open it, for a page decided by a permission or a login's kind
   * rather than a stored tab (no `nav_tabs` key was added for it).
   */
  visible?: (user: NonNullable<NavUser>) => boolean;
  /**
   * With `rail: false`: on the rail after all for someone who may not open
   * this page, so they still have a way in. Buyers and Suppliers are views
   * of Companies; a member given Buyers but not Companies keeps the item.
   */
  railWithout?: string;
};
export type NavGroup = {
  key: string;
  heading?: NavLabelKey;
  /** Folds away; the choice is remembered in this browser. */
  collapsible?: boolean;
  /** Folded until someone opens it. */
  startCollapsed?: boolean;
  items: NavEntry[];
};

export const NAV: NavGroup[] = [
  {
    key: 'home',
    items: [
      { to: '/', labelKey: 'home', icon: IconHome, tab: 'dashboard' },
    ],
  },
  {
    // Everything held: the rows that arrive, the people and companies they
    // are about, and the deals worked from them. Not collapsible: this is
    // where most of the day is spent.
    key: 'work',
    heading: 'data',
    items: [
      // The records page, "Rows" (One Rowstorm, step 3). Its saved filters
      // (segments) live in its side rail. `/rows` is an address for it too.
      {
        to: '/leads', labelKey: 'records', icon: IconList, tab: 'leads',
        alsoActive: ['/people', '/segments', '/rows'],
      },
      // The same page on each person. The two items are the switch; the
      // page shows none of its own.
      {
        to: '/people', href: '/leads?view=people', labelKey: 'people', icon: IconUserCircle, tab: 'people',
      },
      { to: '/companies', labelKey: 'companies', icon: IconBuilding, tab: 'companies' },
      // Buyers and suppliers are companies with a role: Companies filtered
      // to data buyers or data sellers. Off the rail for anyone who has
      // Companies (the role filter is there); kept for anyone who has only
      // these. Their tab keys stay `buyers` and `suppliers`, so who may
      // open them is unchanged.
      {
        to: '/buyers', href: '/companies?role=data_buyer', labelKey: 'buyers', icon: IconBuildingStore,
        tab: 'buyers', rail: false, railWithout: '/companies',
      },
      {
        to: '/suppliers', href: '/companies?role=data_seller', labelKey: 'suppliers', icon: IconAddressBook,
        tab: 'suppliers', rail: false, railWithout: '/companies',
      },
      { to: '/deals', labelKey: 'deals', icon: IconBriefcase, tab: 'deals' },
    ],
  },
  {
    key: 'setup',
    heading: 'setUp',
    collapsible: true,
    items: [
      { to: '/campaigns', labelKey: 'campaigns', icon: IconSpeakerphone, tab: 'campaigns', alsoActive: ['/streams'] },
      // Every route of every stream. The item Destinations used to be, so
      // the same `destinations` tab decides who sees it; the transport list
      // (/destinations) is part of it.
      { to: '/routes', labelKey: 'routes', icon: IconRoute, tab: 'destinations', alsoActive: ['/destinations'] },
      // No tab key: stored nav_tabs predate it. Never for a scoped login
      // (see UNSCOPED_ONLY), as the server refuses them every workflow.
      { to: '/automations', labelKey: 'workflows', icon: IconBolt },
      // Import and verify are things you do to a file, started from the
      // file itself; their history lives here. Kept on the rail (and on the
      // Streams page) rather than folded into Streams, so nobody loses it.
      { to: '/sheets', labelKey: 'imports', icon: IconTable, tab: 'import' },
      { to: '/emails', labelKey: 'email', icon: IconMail, tab: 'campaigns' },
    ],
  },
  {
    key: 'money',
    heading: 'money',
    collapsible: true,
    items: [
      // Money after the sale. No tab keys: who sees them is a permission
      // (manage_returns, manage_billing) or being a buyer or supplier login,
      // and, for staff, there being money to see (`seesMoney`).
      { to: '/returns', labelKey: 'returns', icon: IconArrowBackUp, visible: (u) => seesReturns(u) },
      { to: '/statements', labelKey: 'statements', icon: IconFileInvoice, visible: (u) => seesStatements(u) },
      { to: '/reports', labelKey: 'reports', icon: IconReportAnalytics, tab: 'reports' },
    ],
  },
  {
    key: 'more',
    heading: 'more',
    collapsible: true,
    startCollapsed: true,
    items: [
      { to: '/logs', labelKey: 'activity', icon: IconHistory, tab: 'campaigns' },
      { to: '/alerts', labelKey: 'alerts', icon: IconBell, tab: 'alerts' },
    ],
  },
  {
    key: 'system',
    items: [
      // Only a super admin can change anything here, and the page says so for
      // everyone else rather than vanishing: a menu item that appears for some
      // people and not others is harder to talk about over the phone.
      { to: '/settings', labelKey: 'settings', icon: IconSettings },
    ],
  },
  {
    // Pages with no rail item of their own. Segments are the records page's
    // side rail; tasks are the top bar's tasks button and the command bar.
    // Endpoints (Destinations) are the transport records, reached from Routes.
    key: 'pages',
    items: [
      // Saved filters over the same rows, so the same `leads` key gates it.
      { to: '/segments', labelKey: 'segments', icon: IconFilter, tab: 'leads', rail: false },
      { to: '/tasks', labelKey: 'tasks', icon: IconChecklist, tab: 'tasks', rail: false },
      { to: '/destinations', labelKey: 'destinations', icon: IconSend, tab: 'destinations', rail: false },
    ],
  },
];

/** Roles that always get the whole menu, whatever their saved tab list says. */
export const FULL_MENU = new Set(['super_admin', 'admin', 'manager']);

export type NavUser = {
  role?: string | null;
  nav_tabs?: string[] | null;
  allowed_buyer_ids?: string[] | null;
  allowed_supplier_ids?: string[] | null;
  allowed_campaign_ids?: string[] | null;
  /**
   * Whether the organisation has money to show. Absent (not loaded yet, or
   * unreadable) shows the Money items to whoever may see them, as before.
   */
  money?: MoneyPresence | null;
  actions?: string[] | null;
} | null;

/**
 * Pages about the whole organisation's records, which a login limited to
 * some rows never sees, whatever its tabs say.
 */
const UNSCOPED_ONLY = new Set(['/people', '/automations']);

/** Roles that only ever see their own slice of the rows. */
const SCOPED_ROLES = new Set(['buyer', 'supplier', 'unapproved']);

/**
 * A login limited to some rows: a buyer, supplier or pending login, or any
 * member given a list of buyers, suppliers or campaigns. People cut across
 * every row, so such a login sees none of them, whatever its tabs say.
 */
export function isScopedUser(user: NavUser): boolean {
  if (!user) return true;
  return SCOPED_ROLES.has(user.role ?? '')
    || user.allowed_buyer_ids != null
    || user.allowed_supplier_ids != null
    || user.allowed_campaign_ids != null;
}

/**
 * Whether this person holds an action, as the server's `user_can` decides:
 * admins hold every action; anyone else the list on their session. A
 * session saved before `actions` was falls back to the role's defaults
 * (managers hold all but billing).
 */
export function hasAction(user: NavUser, action: string): boolean {
  if (!user) return false;
  if (user.role === 'super_admin' || user.role === 'admin') return true;
  if (user.actions) return user.actions.includes(action);
  return user.role === 'manager' && action !== 'manage_billing';
}

/** A buyer portal login: buyers assigned, no suppliers. */
export function isBuyerLogin(user: NavUser): boolean {
  return !!user && (user.allowed_buyer_ids?.length ?? 0) > 0 && user.allowed_supplier_ids == null;
}

/** A supplier portal login: suppliers assigned, no buyers. */
export function isSupplierLogin(user: NavUser): boolean {
  return !!user && (user.allowed_supplier_ids?.length ?? 0) > 0 && user.allowed_buyer_ids == null;
}

/**
 * Whether there is money here to look at: a stream that sells (on or
 * paused), or returns or statements already on record. Not known yet reads
 * as yes, so the menu never drops an item and puts it back.
 */
export function seesMoney(user: NavUser, kind: 'returns' | 'statements'): boolean {
  const m = user?.money;
  if (!m) return true;
  return m.any_stream_sells || (kind === 'returns' ? m.has_returns : m.has_statements);
}

/**
 * Staff who run returns, where there is money; or a buyer login (which asks
 * for them).
 */
export function seesReturns(user: NavUser): boolean {
  return (!isScopedUser(user) && hasAction(user, 'manage_returns') && seesMoney(user, 'returns'))
    || isBuyerLogin(user);
}

/**
 * Staff who run billing, where there is money; or a buyer or supplier login
 * (its own statements).
 */
export function seesStatements(user: NavUser): boolean {
  return (!isScopedUser(user) && hasAction(user, 'manage_billing') && seesMoney(user, 'statements'))
    || isBuyerLogin(user) || isSupplierLogin(user);
}

/**
 * Every page this person may open, rail item or not: a buyer should not be
 * shown Campaigns. The tabs decide.
 *
 * The records page is open to anyone with leads, or with people (who then
 * see it on its people view only). The records page is second.
 */
export function navFor(user: NavUser) {
  const nav = NAV;
  if (!user) return nav;
  const scoped = isScopedUser(user);
  const full = FULL_MENU.has(user.role ?? '');
  const tabs = new Set(user.nav_tabs ?? []);
  const has = (tab?: string) => full || !tab || tabs.has(tab);
  return nav
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => {
        if (UNSCOPED_ONLY.has(i.to) && scoped) return false;
        if (i.visible) return i.visible(user);
        if (i.to === '/leads') return has('leads') || (!scoped && has('people'));
        return has(i.tab);
      }),
    }))
    .filter((g) => g.items.length > 0);
}

/** The rail itself: the pages that have an item of their own. */
export function railFor(user: NavUser) {
  const nav = navFor(user);
  const paths = new Set(nav.flatMap((g) => g.items.map((i) => i.to)));
  const onRail = (i: NavEntry) =>
    i.rail !== false || (!!i.railWithout && !paths.has(i.railWithout));
  return nav
    .map((g) => ({ ...g, items: g.items.filter(onRail) }))
    .filter((g) => g.items.length > 0);
}

/** Whether this person may see lead rows at all, not just the people behind them. */
export function seesLeadRows(user: NavUser): boolean {
  if (!user) return true;
  return FULL_MENU.has(user.role ?? '') || (user.nav_tabs ?? []).includes('leads');
}

export const isActive = (to: string, pathname: string) => (to === '/' ? pathname === '/' : pathname.startsWith(to));

/** The rail item's link: its `href` when it has one, else its page. */
export const linkOf = (entry: NavEntry) => entry.href ?? entry.to;

/**
 * Whether this address is the view an entry's `href` names: the same path,
 * and every query value it sets. `/companies?role=data_buyer&q=esb` is the
 * Buyers view; `/companies` and `/companies/c1` are not.
 */
function hrefOpen(entry: NavEntry, pathname: string, search: string): boolean {
  if (!entry.href) return false;
  const url = new URL(entry.href, 'http://rail');
  if (pathname !== url.pathname) return false;
  const here = new URLSearchParams(search);
  return [...url.searchParams].every(([k, val]) => here.get(k) === val);
}

const ALL_ENTRIES = () => NAV.flatMap((g) => g.items);

/**
 * Whether a rail item should light up here: its own page, or one that is
 * part of it. An item with an `href` lights up on that view; the page it is
 * a view of then does not, so Companies is not lit beside Buyers.
 */
export const railActive = (
  entry: NavEntry, pathname: string, search = '',
  shown: NavEntry[] = ALL_ENTRIES().filter((e) => e.rail !== false),
) => {
  if (entry.href) return hrefOpen(entry, pathname, search);
  // Only an item on the rail takes the light away: People takes it from
  // Rows (or Leads) on the people view, and Companies stays lit on the
  // Buyers view unless Buyers is on this person's rail.
  if (shown.some((e) => e !== entry && hrefOpen(e, pathname, search))) return false;
  return isActive(entry.to, pathname) || (entry.alsoActive ?? []).some((p) => isActive(p, pathname));
};

/** The nav entry an address belongs to, for the page name in the top bar. */
export function entryFor(pathname: string, search = ''): NavEntry | undefined {
  return ALL_ENTRIES().find((i) => hrefOpen(i, pathname, search))
    ?? ALL_ENTRIES().find((i) => isActive(i.to, pathname));
}

/**
 * Whether this person may be on this address, from the pages they may open
 * (`navFor`). A company's pages are also open to anyone with Buyers or
 * Suppliers, as those are company views now; the server still decides what
 * each one shows.
 */
export function mayOpen(nav: NavGroup[], pathname: string): boolean {
  const items = nav.flatMap((g) => g.items);
  if (items.some((i) => isActive(i.to, pathname))) return true;
  if (isActive('/companies', pathname)) return items.some((i) => i.to === '/buyers' || i.to === '/suppliers');
  return !NAV.some((g) => g.items.some((i) => i.to !== '/' && pathname.startsWith(i.to)));
}
